/** Warm scene illustrations for the marketing page. Replace SVGs with photos when ready. */
export const LANDING_SCENES = {
  hero: '/images/landing/hero.webp',
  storyBackground: '/images/landing/story-background.webp',
  heroWelcome: '/images/landing/scene-hero-welcome.svg',
  heroTogether: '/images/landing/scene-hero-together.svg',
  heroCare: '/images/landing/scene-hero-care.svg',
  momentOffice: '/images/landing/scene-moment-office.svg',
  momentMembers: '/images/landing/scene-moment-members.svg',
  momentGuests: '/images/landing/scene-moment-guests.svg',
  momentGiving: '/images/landing/scene-moment-giving.svg',
  stepStart: '/images/landing/scene-step-start.svg',
  stepChurch: '/images/landing/scene-step-church.svg',
  stepTeam: '/images/landing/scene-step-team.svg',
} as const;

export const LANDING_SURFACES = {
  console: '/images/landing/dashboard.png',
  app: '/images/landing/app.png',
  kiosk: '/images/landing/feature-kiosk-checkin.png',
  give: '/images/landing/feature-giving-link.png',
} as const;

/** Product screenshots for the features page and marketing surfaces. */
export const LANDING_FEATURE_IMAGES = {
  dashboardCustomize: '/images/landing/feature-dashboard-customize.png',
  dashboard: '/images/landing/dashboard.png',
  people: '/images/landing/feature-people.png',
  group: '/images/landing/feature-group.png',
  committee: '/images/landing/feature-committee.png',
  marriage: '/images/landing/feature-marriage.png',
  content: '/images/landing/feature-content.png',
  fund: '/images/landing/feature-fund.png',
  givingLink: '/images/landing/feature-giving-link.png',
  kioskCheckin: '/images/landing/feature-kiosk-checkin.png',
  role: '/images/landing/feature-role.png',
  app: '/images/landing/app.png',
} as const;

export type LandingFeatureScreenshot = {
  src: string;
  width: number;
  height: number;
  altKey:
    | `images.features.${'dashboard' | 'people' | 'roles' | 'group' | 'committee' | 'register' | 'content' | 'giving' | 'app'}`;
};

/** Native pixel dimensions — used for layout so screenshots are never cropped. */
export const LANDING_FEATURE_SCREENSHOTS = {
  dashboardCustomize: {
    src: LANDING_FEATURE_IMAGES.dashboardCustomize,
    width: 1024,
    height: 666,
    altKey: 'images.features.dashboard',
  },
  people: {
    src: LANDING_FEATURE_IMAGES.people,
    width: 1024,
    height: 681,
    altKey: 'images.features.people',
  },
  role: {
    src: LANDING_FEATURE_IMAGES.role,
    width: 1024,
    height: 636,
    altKey: 'images.features.roles',
  },
  group: {
    src: LANDING_FEATURE_IMAGES.group,
    width: 887,
    height: 1024,
    altKey: 'images.features.group',
  },
  committee: {
    src: LANDING_FEATURE_IMAGES.committee,
    width: 957,
    height: 1024,
    altKey: 'images.features.committee',
  },
  marriage: {
    src: LANDING_FEATURE_IMAGES.marriage,
    width: 940,
    height: 1024,
    altKey: 'images.features.register',
  },
  content: {
    src: LANDING_FEATURE_IMAGES.content,
    width: 1024,
    height: 432,
    altKey: 'images.features.content',
  },
  fund: {
    src: LANDING_FEATURE_IMAGES.fund,
    width: 722,
    height: 1024,
    altKey: 'images.features.giving',
  },
  app: {
    src: LANDING_FEATURE_IMAGES.app,
    width: 1290,
    height: 2796,
    altKey: 'images.features.app',
  },
} as const satisfies Record<string, LandingFeatureScreenshot>;

/** Console dashboard screenshot for marketing previews. */
export const LANDING_DASHBOARD = LANDING_FEATURE_IMAGES.dashboard;

/** Congregant app screenshot for marketing previews. */
export const LANDING_APP = LANDING_FEATURE_IMAGES.app;

export type LandingSceneKey = keyof typeof LANDING_SCENES;
export type LandingSurfaceKey = keyof typeof LANDING_SURFACES;

export type LandingSurfaceScreenshot = {
  src: string;
  width: number;
  height: number;
  altKey: `images.surfaces.${LandingSurfaceKey}`;
  audienceKey: 'office' | 'members' | 'guests' | 'giving';
};

/** Native dimensions for surface cards — images scale down, never crop. */
export const LANDING_SURFACE_SCREENSHOTS = {
  console: {
    src: LANDING_SURFACES.console,
    width: 2048,
    height: 1060,
    altKey: 'images.surfaces.console',
    audienceKey: 'office',
  },
  app: {
    src: LANDING_SURFACES.app,
    width: 1290,
    height: 2796,
    altKey: 'images.surfaces.app',
    audienceKey: 'members',
  },
  kiosk: {
    src: LANDING_SURFACES.kiosk,
    width: 746,
    height: 1024,
    altKey: 'images.surfaces.kiosk',
    audienceKey: 'guests',
  },
  give: {
    src: LANDING_SURFACES.give,
    width: 998,
    height: 886,
    altKey: 'images.surfaces.give',
    audienceKey: 'giving',
  },
} as const satisfies Record<LandingSurfaceKey, LandingSurfaceScreenshot>;
